import {
  customerTypeEnum,
  opportunityStageEnum,
  propertyTypeEnum,
  visitAttendees,
  visitCommissions,
  visits,
  visitTasks,
  visitTaskSteps,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { withAttachments } from './attachment'
import { phoneNumberSchema } from './common'
import {
  ilmanvaihtoFormValueSchema,
  nuohousFormValueSchema,
} from './servicePreset'
import { acquisitionDataSchema, attributionPayloadSchema } from './acquisition'
import { selectVisitLineItemWithProductSchema } from './visitLineItem'

// Predefined cancellation reasons. Selected reasons (or free-text "Muu syy"
// content) are persisted verbatim into visits.cancelReason — the dropdown is
// just a UI shortcut for the most common cases.
export const VISIT_CANCEL_REASONS = [
  'Asiakas perui',
  'Asiakas ei tavoitettu',
  'Kohteeseen ei pääsyä',
  'Asiakas siirsi käynnin',
  'Sääolosuhteet',
  'Työntekijä estynyt',
  'Tuplabuukkaus',
] as const

// Work content item schema
export const workContentItemSchema = z.object({
  key: z.string(),
  label: z.string().optional(),
})

export type WorkContentItem = z.infer<typeof workContentItemSchema>

// Ventilation measurement protocol (Mittauspöytäkirja) schemas
export const airMeasurementSchema = z.object({
  quantity: z.string().optional(),
  valveType: z.string().optional(),
  airSpeed: z.string().optional(),
  pressure: z.string().optional(),
  position: z.string().optional(),
  kValue: z.string().optional(),
  adjustedFlow: z.string().optional(),
  requiredFlow: z.string().optional(),
  differenceLs: z.string().optional(),
  differencePercent: z.string().optional(),
  ductShape: z.enum(['round', 'rectangular']).optional(),
  ductDiameter: z.string().optional(),
  ductWidth: z.string().optional(),
  ductHeight: z.string().optional(),
})

export const exhaustAirMeasurementSchema = airMeasurementSchema.extend({
  envelopePressure: z.string().optional(),
})

export const measurementRoomSchema = z.object({
  id: z.string(),
  name: z.string(),
  supplyAir: airMeasurementSchema.optional(),
  exhaustAir: exhaustAirMeasurementSchema.optional(),
  notes: z.string().optional(),
})

export const mittauspoytakirjaDataSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  measurementDate: z.string().optional(),
  measurers: z.string().optional(),
  outdoorTemperature: z.string().optional(),
  windSpeed: z.string().optional(),
  measurementDevices: z.string().optional(),
  calibrationValid: z.string().optional(),
  hvacMachines: z.string().optional(),
  measurementSpeed: z.string().optional(),
  planDate: z.string().optional(),
  rooms: z.array(measurementRoomSchema),
  uploadedPdfAttachmentId: z.number().optional(),
})

// Auto-generate base schemas
export const selectVisitSchema = createSelectSchema(visits, {
  // Make internal fields optional so they can be filtered out (set to undefined)
  internalNotes: z.string().nullable().optional(),
  internalSafetyNotices: z.string().nullable().optional(),
  internalSafetyNoticesReviewedAt: z.string().nullable().optional(),
  internalSafetyNoticesReviewedBy: z.number().nullable().optional(),
  internalSafetyNoticesReviewComment: z.string().nullable().optional(),
  internalSalesNotes: z.string().nullable().optional(),
  // INV-7: role-gated attribution column
  internalAcquisitionData: z.unknown().optional(),
  workContent: z.array(workContentItemSchema).nullable().optional(),
  measurementReportData: z
    .array(mittauspoytakirjaDataSchema)
    .nullable()
    .optional(),
})
export const insertVisitSchema = createInsertSchema(visits, {
  workContent: z.array(workContentItemSchema).nullable().optional(),
  // Backwards compatibility: accept both single object (old format) and array (new format)
  measurementReportData: z.preprocess((val) => {
    if (val === null || val === undefined) return val
    const arr = Array.isArray(val) ? val : [val]
    return arr.map((item: Record<string, unknown>) =>
      item.id ? item : { ...item, id: crypto.randomUUID() },
    )
  }, z.array(mittauspoytakirjaDataSchema).nullable().optional()),
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitTaskSchema = createSelectSchema(visitTasks)
export const insertVisitTaskSchema = createInsertSchema(visitTasks).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitAttendeeSchema = createSelectSchema(visitAttendees)
export const insertVisitAttendeeSchema = createInsertSchema(
  visitAttendees,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitTaskStepSchema = createSelectSchema(visitTaskSteps)
export const insertVisitTaskStepSchema = createInsertSchema(
  visitTaskSteps,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})

export const selectVisitCommissionSchema = createSelectSchema(visitCommissions)
export const insertVisitCommissionSchema = createInsertSchema(
  visitCommissions,
).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
})
export const updateVisitCommissionSchema = insertVisitCommissionSchema.partial()

// Visit task with steps
export const selectVisitTaskWithStepsSchema = selectVisitTaskSchema.extend({
  steps: z.array(selectVisitTaskStepSchema).optional(),
})

// Visit with relations
export const selectVisitWithRelationsSchema = withAttachments(
  selectVisitSchema.extend({
    subscription: z
      .object({
        id: z.number(),
        propertyId: z.number(),
        displayName: z.string().nullable().optional(),
        startDate: z.string(),
        endDate: z.string().nullable(),
        status: z.enum(['proposal', 'active', 'paused', 'cancelled']),
        additionalTerms: z.string().nullable(),
        subscriptionPackageVersions: z
          .array(
            z.object({
              servicePackageVersionId: z.number(),
              sortOrder: z.number().nullable(),
            }),
          )
          .optional(),
      })
      .nullable()
      .optional(),
    property: z
      .object({
        id: z.number(),
        customerId: z.number(),
        propertyType: z.enum(propertyTypeEnum.enumValues),
        customer: z.object({
          id: z.number(),
          name: z.string(),
          type: z.enum(customerTypeEnum.enumValues),
          billingMethod: z.enum(['email', 'einvoice']),
          billingEmail: z.string().nullable(),
          members: z
            .array(
              z.object({
                id: z.number(),
                role: z.enum(['owner', 'member']),
                user: z.object({
                  id: z.number(),
                  firstName: z.string(),
                  lastName: z.string(),
                  email: z.string().nullable(),
                  phoneNumber: z.string().nullable(),
                }),
              }),
            )
            .optional(),
        }),
        address: z
          .object({
            id: z.number(),
            street: z.string(),
            city: z.string(),
            postalCode: z.string().nullable(),
            country: z.string().nullable(),
            latitude: z.string().nullable(),
            longitude: z.string().nullable(),
          })
          .optional(),
      })
      .nullable()
      .optional(),
    opportunity: z
      .object({
        id: z.number(),
        stage: z.enum(opportunityStageEnum.enumValues),
        customerId: z.number().nullable(),
        user: z.object({
          id: z.number(),
          firstName: z.string(),
          lastName: z.string(),
          email: z.string().nullable(),
          phoneNumber: z.string().nullable(),
        }),
      })
      .nullable()
      .optional(),
    visitTasks: z
      .array(
        selectVisitTaskSchema.extend({
          steps: z.array(selectVisitTaskStepSchema).optional(),
          maintenanceTask: z
            .object({
              id: z.number(),
              name: z.string(),
              description: z.string().nullable(),
              category: z.string().nullable(),
              estimatedDurationMinutes: z.number().nullable(),
              price: z.string().nullable(),
            })
            .optional(),
          billedByUser: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
    visitLineItems: z.array(selectVisitLineItemWithProductSchema).optional(),
    visitAttendees: z
      .array(
        selectVisitAttendeeSchema.extend({
          user: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
              email: z.string(),
            })
            .optional(),
        }),
      )
      .optional(),
    visitCommissions: z
      .array(
        selectVisitCommissionSchema.extend({
          user: z
            .object({
              id: z.number(),
              firstName: z.string(),
              lastName: z.string(),
              email: z.string().nullable(),
            })
            .optional(),
          visitTask: z
            .object({
              id: z.number(),
              taskName: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
    feedbacks: z
      .array(
        z.object({
          id: z.number(),
          ratingValue: z.number(),
          description: z.string().nullable().optional(),
          feedbackTagMappings: z
            .array(
              z.object({
                id: z.number(),
                feedbackTagId: z.number(),
                feedbackTag: z
                  .object({
                    id: z.number(),
                    name: z.string(),
                    category: z.string().nullable().optional(),
                  })
                  .optional(),
              }),
            )
            .optional(),
        }),
      )
      .optional(),
    salesUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
      })
      .nullable()
      .optional(),
    internalSafetyNoticesReviewedByUser: z
      .object({
        id: z.number(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      })
      .nullable()
      .optional(),
    // Invoices related to this visit (PDF download via separate invoice.getInvoicePdf endpoint)
    // Soft-deleted invoices filtered automatically by addSoftDeleteFiltering
    invoices: z
      .array(
        z.object({
          id: z.number(),
          createdAt: z.string(),
          invoiceNumber: z.string(),
          referenceNumber: z.string(), // Finnish bank reference - required for payment
          totalAmount: z.string(),
          status: z.enum(['finalized', 'sent', 'paid', 'void']),
          dueDate: z.string(),
          sentAt: z.string().nullable(),
          paidAt: z.string().nullable(),
          // Invoice this one replaces (for correction invoices)
          voidedInvoice: z
            .object({
              invoiceNumber: z.string(),
            })
            .nullable()
            .optional(),
        }),
      )
      .optional(),
  }),
)

// Input schema for creating visit with attendees
export const createVisitWithAttendeesSchema = insertVisitSchema.extend({
  attendees: z
    .array(
      z.object({
        userId: z.number(),
        role: z
          .enum(['lead', 'assistant', 'trainee', 'instructor', 'participant'])
          .optional(),
        status: z.enum(['confirmed', 'tentative', 'declined']).optional(),
        notes: z.string().optional(),
      }),
    )
    .optional(),
})

// Update schemas
export const updateVisitSchema = insertVisitSchema.partial()
export const updateVisitTaskSchema = insertVisitTaskSchema.partial()
export const updateVisitAttendeeSchema = insertVisitAttendeeSchema.partial()
export const updateVisitTaskStepSchema = insertVisitTaskStepSchema.partial()

// Safety notices review schema
export const markSafetyNoticesReviewedSchema = z.object({
  id: z.number(),
  reviewComment: z.string().optional(),
})

// Fireplace selection with quantity
export const fireplaceSelectionSchema = z.object({
  type: z.string(),
  quantity: z.number().int().positive().default(1),
})

// Visit task metadata schemas (for forms and certificates)
export const nuohoustodistusDataSchema = z.object({
  // Fireplace types that were swept (with quantities)
  fireplaceTypes: z.array(fireplaceSelectionSchema),

  // Fireplaces and flues assessment
  fireplaceStatus: z.enum(['ok', 'deficiency']),
  fireplaceComments: z.string().optional(),

  // Roof safety assessment
  roofSafety: z.enum(['ok', 'deficiency', 'not_ok_not_performed']),
  roofSafetyComments: z.string().optional(),

  // Action required when deficiencies are found
  actionRequired: z
    .enum(['customer_guidance', 'notify_fire_department'])
    .optional(),

  // Next sweeping deadline
  nextSweepingDeadline: z.enum(['1_year', '3_years']),

  // General inspector notes
  inspectorNotes: z.string().optional(),
})

export const visitTaskMetadataSchema = z.discriminatedUnion('formType', [
  z.object({
    formType: z.literal('nuohoustodistus'),
    data: nuohoustodistusDataSchema,
  }),
  z.object({
    formType: z.literal('mittauspoytakirja'),
    data: mittauspoytakirjaDataSchema,
  }),
])

// Public booking schemas
export const bookSurveyVisitSchema = z.object({
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  startTime: z.string().datetime(), // ISO datetime for the booking
  userId: z.number(),
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  email: z.string().email('Virheellinen sähköpostiosoite'),
  phoneNumber: phoneNumberSchema,
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  propertyType: z.enum(propertyTypeEnum.enumValues, {
    errorMap: () => ({ message: 'Kiinteistön tyyppi on pakollinen' }),
  }),
  notes: z.string().optional(),
  acquisitionData: acquisitionDataSchema.optional(),
})

export const bookSurveyVisitAuthenticatedSchema = z.object({
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  startTime: z.string().datetime(),
  userId: z.number(), // Employee ID
  propertyId: z.number().optional(),
  // User can update their contact details
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  phoneNumber: z.string().optional(),
  // Address fields when propertyId is not provided
  street: z.string().optional(),
  postalCode: z.string().optional(),
  propertyType: z.enum(propertyTypeEnum.enumValues).optional(),
  notes: z.string().optional(),
  acquisitionData: acquisitionDataSchema.optional(),
})

export const getAvailableTimeSlotsSchema = z.object({})

const getTimeSlotsForAddressBaseSchema = z.object({
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  maxDate: z.string().datetime().optional(),
})

/** Duration is computed server-side from presetFormValue — no client-supplied duration. */
export const getTimeSlotsForAddressSchema = z.discriminatedUnion(
  'servicePresetKey',
  [
    getTimeSlotsForAddressBaseSchema.extend({
      servicePresetKey: z.literal('nuohous'),
      presetFormValue: nuohousFormValueSchema,
    }),
    getTimeSlotsForAddressBaseSchema.extend({
      servicePresetKey: z.literal('ilmanvaihto'),
      presetFormValue: ilmanvaihtoFormValueSchema,
    }),
  ],
)

// Common customer + address fields shared across all preset bookings
const bookServiceVisitBaseSchema = z.object({
  startTime: z.string().datetime(),
  employeeId: z.number(),
  firstName: z.string().min(1, 'Etunimi on pakollinen'),
  lastName: z.string().min(1, 'Sukunimi on pakollinen'),
  email: z.string().email('Virheellinen sähköpostiosoite'),
  phoneNumber: phoneNumberSchema,
  street: z.string().min(1, 'Osoite on pakollinen'),
  postalCode: z.string().min(1, 'Postinumero on pakollinen'),
  city: z.string().min(1, 'Kaupunki on pakollinen'),
  propertyType: z.enum(propertyTypeEnum.enumValues, {
    errorMap: () => ({ message: 'Kiinteistön tyyppi on pakollinen' }),
  }),
  notes: z.string().optional(),
  /**
   * Canonical attribution payload from the client's mk_attr cookie.
   * firstTouch → customer.internalFirstTouchAcquisition (via user), lastTouch
   * → visit.internalAcquisitionData. See docs/attribution-semantics.md §3.
   */
  attribution: attributionPayloadSchema.optional(),
  /**
   * Legacy flat-UTM field — MUST remain until the `booking-widget-inline.iife.js`
   * bundles currently shipped on markku.fi and nuohousmarkku.fi are rebuilt
   * from this branch's source and redeployed. Those bundles send
   * `{ utmParams: {...} }` and without this field the strict schema would
   * 400 every live booking. Remove after both sites ship the new IIFE.
   */
  utmParams: z.record(z.string()).optional(),
})

// Server-authoritative booking schema: the client picks a preset and sends
// only the form values they chose. The backend computes tasks, duration, and
// per-task pricing from those values, so the client cannot manipulate the
// price, the task IDs, or the visit length.
//
// Default Zod behavior (strip) is used deliberately — NOT `.strict()`.
// Unknown fields from stale embedded bundles (e.g. markku.fi /
// nuohousmarkku.fi's cached booking-widget-inline.iife.js that may ship a
// new field before the backend knows about it) are silently dropped rather
// than 400'd. Server-authoritative pricing still holds: since `price`,
// `maintenanceTaskIds`, `durationMinutes` etc. aren't in the schema, Zod
// strips them before the handler sees them — client can't smuggle them in.
// Strict mode would only loudly reject instead of silently stripping; same
// security, worse resilience.
export const bookServiceVisitSchema = z.discriminatedUnion('servicePresetKey', [
  bookServiceVisitBaseSchema.extend({
    servicePresetKey: z.literal('nuohous'),
    presetFormValue: nuohousFormValueSchema,
  }),
  bookServiceVisitBaseSchema.extend({
    servicePresetKey: z.literal('ilmanvaihto'),
    presetFormValue: ilmanvaihtoFormValueSchema,
  }),
])

export const availableTimeSlotSchema = z.object({
  startTime: z.string().datetime(),
  endTime: z.string().datetime(),
  userId: z.number(),
})

// Type exports
export type Visit = z.infer<typeof selectVisitSchema>
export type VisitWithRelations = z.infer<typeof selectVisitWithRelationsSchema>
export type VisitTask = z.infer<typeof selectVisitTaskSchema>
export type VisitTaskStep = z.infer<typeof selectVisitTaskStepSchema>
export type VisitTaskWithSteps = z.infer<typeof selectVisitTaskWithStepsSchema>
export type VisitAttendee = z.infer<typeof selectVisitAttendeeSchema>
export type CreateVisit = z.infer<typeof insertVisitSchema>
export type UpdateVisit = z.infer<typeof updateVisitSchema>
export type CreateVisitTask = z.infer<typeof insertVisitTaskSchema>
export type UpdateVisitTask = z.infer<typeof updateVisitTaskSchema>
export type CreateVisitTaskStep = z.infer<typeof insertVisitTaskStepSchema>
export type UpdateVisitTaskStep = z.infer<typeof updateVisitTaskStepSchema>
export type CreateVisitWithAttendees = z.infer<
  typeof createVisitWithAttendeesSchema
>
export type BookSurveyVisit = z.infer<typeof bookSurveyVisitSchema>
export type BookSurveyVisitAuthenticated = z.infer<
  typeof bookSurveyVisitAuthenticatedSchema
>
export type BookServiceVisit = z.infer<typeof bookServiceVisitSchema>
export type GetTimeSlotsForAddress = z.infer<
  typeof getTimeSlotsForAddressSchema
>
export type AvailableTimeSlot = z.infer<typeof availableTimeSlotSchema>
export type FireplaceSelection = z.infer<typeof fireplaceSelectionSchema>
export type NuohoustodistusData = z.infer<typeof nuohoustodistusDataSchema>
export type AirMeasurement = z.infer<typeof airMeasurementSchema>
export type ExhaustAirMeasurement = z.infer<typeof exhaustAirMeasurementSchema>
export type MeasurementRoom = z.infer<typeof measurementRoomSchema>
export type MittauspoytakirjaData = z.infer<typeof mittauspoytakirjaDataSchema>
export type VisitTaskMetadata = z.infer<typeof visitTaskMetadataSchema>
export type VisitCommission = z.infer<typeof selectVisitCommissionSchema>
export type CreateVisitCommission = z.infer<typeof insertVisitCommissionSchema>
export type UpdateVisitCommission = z.infer<typeof updateVisitCommissionSchema>
export type MarkSafetyNoticesReviewed = z.infer<
  typeof markSafetyNoticesReviewedSchema
>
